import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { setToken, removeToken } from "../../utils/auth";
import {
  AdminDetail,
  AdminListItem,
  AdminLoginResponse,
  AdminProfile,
  ApiResponse,
  CreateAdminRequest,
  LoginCredentials,
} from "../../types";
import { toast } from "../../core/toast";
import { setAdminLogin, setAdminProfile } from "./adminReducer";
import { AdminLoginTokenResponse } from "../../types/admin/admin.interfaces";

// ---------- API ----------
export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: baseQueryWithAuth, // Use shared base query
  tagTypes: ["Auth", "Profile", "Admins"],

  endpoints: (builder) => ({
    login: builder.mutation<ApiResponse<AdminLoginResponse>, LoginCredentials>({
      query: (credentials) => ({
        url: ApiEndpoints.adminLogin,
        method: "PATCH",
        body: credentials,
      }),
      invalidatesTags: ["Auth", "Profile"],
      transformResponse: (response: ApiResponse<AdminLoginResponse>) => {
        const token = response?.result?.token;
        if (token) {
          setToken(token);
        }
        return response;
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const token = data?.result?.token;
          if (token) {
            dispatch(setAdminLogin(token));
          }
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Failed to login");
          // Error handled globally in baseQueryWithAuth
        }
      },
    }),

    // ---------- PASSWORD OPS ----------
    forgotPassword: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { email: string }
    >({
      query: (body) => ({
        url: `${ApiEndpoints.adminForgetPassword}`,
        method: "PATCH",
        body,
      }),
    }),

    resetPassword: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { currentPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: `${ApiEndpoints.adminResetPassword}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Profile"],
    }),

    // ---------- PROFILE ----------
    getProfile: builder.query<ApiResponse<AdminProfile>, void>({
      query: () => `${ApiEndpoints.adminProfile}`,
      providesTags: ["Profile"],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.result) {
            dispatch(setAdminProfile(data.result));
          }
        } catch (err: any) {
          toast.error(err?.error?.data?.message);

          // Error handled globally in baseQueryWithAuth
        }
      },
    }),

    // ---------- CREATE ADMIN ----------
    createAdmin: builder.mutation<
      ApiResponse<AdminProfile>,
      CreateAdminRequest
    >({
      query: (body) => ({
        url: `${ApiEndpoints.createAdmin}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Admins"],
    }),

    // ---------- LOGOUT ----------
    logout: builder.mutation<ApiResponse<{ ok: boolean }>, void>({
      query: () => ({
        url: `${ApiEndpoints.adminLogout}`,
        method: "PATCH",
      }),
      invalidatesTags: ["Auth", "Profile"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err: any) {
          // Error handled globally
        } finally {
          removeToken(); // Remove from cookie
        }
      },
    }),

    // ---------- GET ADMINS ----------
    getAdmins: builder.query<AdminListItem[], void>({
      query: () => ({
        url: `${ApiEndpoints.getAllAdmin}`,
        method: "GET",
      }),
      transformResponse: (res: any) => {
        const list = Array.isArray(res?.results) ? res.results : [];
        const normStatus = (s: any) =>
          String(s ?? "Active").toLowerCase() === "inactive"
            ? "Inactive"
            : "Active";
        const orgFromAssigned = (arr: any[]) => {
          if (!Array.isArray(arr) || arr.length === 0) return "";
          const first = arr[0];
          return first?.name ?? first?.title ?? "";
        };
        return list.map((a: any) => ({
          id: String(a?._id ?? a?.id ?? ""),
          name: a?.name ?? "",
          role: a?.role ?? a?.designation ?? "",
          email: a?.email ?? "",
          phone: String(a?.phone ?? a?.mobile ?? ""),
          org:
            a?.organisation ??
            a?.org ??
            orgFromAssigned(a?.assignedOrganisations) ?? // fixed: pass array, not .name
            "",
          assignedOrganisations: Array.isArray(a?.assignedOrganisations)
            ? a.assignedOrganisations
            : [],
          status: normStatus(a?.status),
        }));
      },
      providesTags: ["Admins"],

    }),

    // ---------- DELETE ADMIN ----------
    deleteAdmin: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.admin}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Admins"],
    }),

    // ---------- GET ADMIN BY ID ----------
    getAdminById: builder.query<AdminDetail, string>({
      query: (id) => ({
        url: `${ApiEndpoints.admin}/${id}`,
        method: "GET",
      }),
      transformResponse: (res: any): AdminDetail => {
        // prefer results[0] if results is an array; else use result/results/object
        const fromResultsArr = Array.isArray(res?.results)
          ? res.results[0]
          : undefined;
        const a = fromResultsArr ?? res?.result ?? res?.results ?? res ?? {};

        const arr = Array.isArray(a?.assignedOrganisations)
          ? a.assignedOrganisations
          : [];
        const firstOrg = arr.length ? arr[0] : undefined;

        const status =
          String(a?.status ?? "Active").toLowerCase() === "inactive"
            ? "Inactive"
            : "Active";

        // Build detail with extra organisation fields for edit defaults
        const detail: any = {
          id: String(a?._id ?? a?.id ?? ""),
          name: a?.name ?? "",
          role: a?.role ?? "",
          designation: a?.designation ?? "",
          email: a?.email ?? "",
          phone: String(a?.phone ?? a?.mobile ?? ""),
          org: firstOrg?.name ?? a?.organisation ?? a?.org ?? "", // keep friendly name
          status,
          isDeleted: Boolean(a?.isDeleted),
          createdAt: a?.createdAt ?? undefined,
          deletedAt: a?.deletedAt ?? null,
          subscriber: a?.subscriber ?? undefined,

          // expose organisation details for edit defaults
          organisationId: firstOrg?._id ?? undefined,
          organisationName: firstOrg?.name ?? undefined,
          organisationCurrency: firstOrg?.currency ?? undefined,
          assignedOrganisations: arr, // pass through for selects
        };

        return detail as AdminDetail;
      },
      providesTags: ["Admins"],

    }),

    // ---------- EDIT ADMIN ----------
    editAdminById: builder.mutation<
      ApiResponse<AdminProfile>,
      { id: string; data: Partial<AdminProfile> }
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.admin}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Admins", "Profile"],
    }),

    // ---------- TOGGLE STATUS ----------
    toggleAdminStatus: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { id: string; status: "Active" | "Inactive" }
    >({
      query: ({ id, status }) => ({
        url: `${ApiEndpoints.adminStatusChange}/${id}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Admins"],
      async onQueryStarted({ id, status }, { dispatch, queryFulfilled }) {
        // Optimistic update in list
        const patchList = dispatch(
          adminApi.util.updateQueryData(
            "getAdmins",
            undefined,
            (draft: Draft<AdminListItem[]>) => {
              const row = draft.find((r) => r.id === id);
              if (row) row.status = status;
            }
          )
        );

        // Optimistic update in detail (if cached)
        const patchDetail = dispatch(
          adminApi.util.updateQueryData(
            "getAdminById",
            id,
            (draft: Draft<AdminDetail>) => {
              if (draft) draft.status = status;
            }
          )
        );

        try {
          await queryFulfilled; // server confirms toggle
        } catch (err: any) {
          // rollback on error
          patchList.undo();
          patchDetail.undo();
        }
      },
    }),
    loginWithToken: builder.mutation<ApiResponse<AdminLoginResponse>, string>({
      query: (token) => ({
        url: ApiEndpoints.adminLoginToken,
        method: "PATCH",
        body: { token },
      }),
      extraOptions: { ignore401: true, hideToast: true },

      // Removing invalidatesTags prevents immediate background queries (like getProfile) 
      // from firing before the token is fully registered or the UI is ready, avoiding instant 401s!
      // invalidatesTags: ["Auth", "Profile"],

      transformResponse: (response: ApiResponse<AdminLoginResponse> | any) => {
        // Look everywhere for the token!
        const token = response?.result?.token || (typeof response?.result === "string" ? response.result : null) || (response as any)?.token;
        if (token) {
          setToken(token);
        } else {
          console.error("Critical: Could not extract token from backend response in transformResponse!", response);
        }
        return response;
      },

      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          
          // Hunt down the token in the same manner
          const newToken = data?.result?.token || (typeof data?.result === "string" ? data.result : null) || (data as any)?.token;

          if (newToken) {
            dispatch(setAdminLogin(newToken));
          } else {
            console.error("Critical: Could not extract token from backend response in onQueryStarted!", data);
          }

          toast.success(data?.message || "Successfully logged in with token");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Token login failed");
        }
      },
    }),


  }),



});

// ---------- EXPORT HOOKS ----------
export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetProfileQuery,
  useCreateAdminMutation,
  useLogoutMutation,
  useGetAdminsQuery,
  useDeleteAdminMutation,
  useGetAdminByIdQuery,
  useEditAdminByIdMutation,
  useToggleAdminStatusMutation,
  useLazyGetProfileQuery,
  useLoginWithTokenMutation,
} = adminApi;
